import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';

const LAST_POST_TIMESTAMP_KEY = 'last_post_timestamp';
const RATE_LIMIT_FREE_MS = 60 * 60 * 1000; // 1 hour in milliseconds
const RATE_LIMIT_PAID_MS = 5 * 60 * 1000; // 5 minutes in milliseconds

export interface RateLimitResult {
  allowed: boolean;
  timeRemaining: number; // in milliseconds
  minutesRemaining: number; // for display
  secondsRemaining: number; // for display
}

/**
 * Get the storage key based on platform
 */
const getStorageKey = (): string => {
  return LAST_POST_TIMESTAMP_KEY;
};

/**
 * Store last post timestamp
 */
export const storeLastPostTimestamp = async (): Promise<void> => {
  try {
    const timestamp = Date.now().toString();
    const key = getStorageKey();
    
    if (Platform.OS === 'web') {
      localStorage.setItem(key, timestamp);
    } else {
      await AsyncStorage.setItem(key, timestamp);
    }
  } catch (error) {
    console.error('Failed to store last post timestamp:', error);
  }
};

/**
 * Get last post timestamp
 */
const getLastPostTimestamp = async (): Promise<number | null> => {
  try {
    const key = getStorageKey();
    let timestampStr: string | null = null;
    
    if (Platform.OS === 'web') {
      timestampStr = localStorage.getItem(key);
    } else {
      timestampStr = await AsyncStorage.getItem(key);
    }
    
    if (!timestampStr) return null;
    
    const timestamp = parseInt(timestampStr, 10);
    return isNaN(timestamp) ? null : timestamp;
  } catch (error) {
    console.error('Failed to get last post timestamp:', error);
    return null;
  }
};

/**
 * Calculate time remaining until next post is allowed
 */
const calculateTimeRemaining = (lastPostTime: number, rateLimitMs: number): number => {
  const now = Date.now();
  const timeSinceLastPost = now - lastPostTime;
  const remaining = rateLimitMs - timeSinceLastPost;
  return Math.max(0, remaining);
};

/**
 * Format time remaining for display
 */
const formatTimeRemaining = (ms: number): { minutes: number; seconds: number } => {
  const minutes = Math.floor(ms / (60 * 1000));
  const seconds = Math.floor((ms % (60 * 1000)) / 1000);
  return { minutes, seconds };
};

/**
 * Check if user can post based on rate limits
 * @param isFreePlan - Whether the user is on a free plan
 * @returns Rate limit result with allowed status and time remaining
 */
export const checkPostRateLimit = async (isFreePlan: boolean): Promise<RateLimitResult> => {
  try {
    const lastPostTime = await getLastPostTimestamp();
    
    // If no previous post, allow posting
    if (!lastPostTime) {
      return {
        allowed: true,
        timeRemaining: 0,
        minutesRemaining: 0,
        secondsRemaining: 0,
      };
    }
    
    const rateLimitMs = isFreePlan ? RATE_LIMIT_FREE_MS : RATE_LIMIT_PAID_MS;
    const timeRemaining = calculateTimeRemaining(lastPostTime, rateLimitMs);
    const { minutes, seconds } = formatTimeRemaining(timeRemaining);
    
    return {
      allowed: timeRemaining === 0,
      timeRemaining,
      minutesRemaining: minutes,
      secondsRemaining: seconds,
    };
  } catch (error) {
    console.error('Failed to check post rate limit:', error);
    // On error, allow posting to avoid blocking users
    return {
      allowed: true,
      timeRemaining: 0,
      minutesRemaining: 0,
      secondsRemaining: 0,
    };
  }
};

/**
 * Format rate limit message for display
 */
export const formatRateLimitMessage = (result: RateLimitResult, isFreePlan: boolean): string => {
  if (result.allowed) {
    return '';
  }
  
  const limitText = isFreePlan ? '1 hour' : '5 minutes';
  
  if (result.minutesRemaining > 0) {
    return `Please wait ${result.minutesRemaining} minute${result.minutesRemaining !== 1 ? 's' : ''} before posting again. Rate limit: ${limitText}.`;
  } else {
    return `Please wait ${result.secondsRemaining} second${result.secondsRemaining !== 1 ? 's' : ''} before posting again. Rate limit: ${limitText}.`;
  }
};
















